import Tool, { type ToolArgsWithController } from '../../../tools/Tool';
import { type Editor, Transforms } from 'slate';
import MediaCommon from '../MediaCommon';
import History from '../../../collaboration/History';
import { isMediaElement } from '../../../types/guards';
import type {
  AttachmentElement,
  AudioElement,
  ImageElement,
  VideoElement,
} from '../../../types/aha-editor';
import AhaEditor from '../../../AhaEditor';
import { getCurrentListItems } from '../../../plugins/ListPlugin/utils';

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

export const ALLOWED_VIDEO_TYPES = [
  'video/quicktime',
  'video/mp4',
  'video/webm',
  'video/ogg',
];

export const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/m4a',
];

export const ALLOWED_MEDIA_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_AUDIO_TYPES,
];

export const ALLOWED_IMAGE_FILE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'gif',
  'png',
  'webp',
];
export const ALLOWED_VIDEO_FILE_EXTENSIONS = ['mov', 'mp4', 'webm', 'ogv'];
export const ALLOWED_AUDIO_FILE_EXTENSIONS = ['mp3', 'wav', 'ogg'];

interface MediaNodeProperties {
  type?: 'image' | 'video' | 'audio' | 'attachment';
  media_src: string;
  resizer_originalHeight?: number;
  resizer_originalWidth?: number;
  resizer_height?: number;
  resizer_width?: number;
  attachment_mimeType?: string;
  attachment_fileName?: string;
}

type UploadableElement =
  | ImageElement
  | VideoElement
  | AudioElement
  | AttachmentElement;

// Convert from Javascript File object to a data URL.
const convertFileToDataURL = async (file: File): Promise<string> => {
  const reader = new FileReader();

  return await new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new DOMException('Problem parsing image file.'));
    };

    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(file);
  });
};

// Insert an image into the editor.
class ToolInsertMedia extends Tool {
  static toolName = 'Image or file';
  static iconClass = 'fa-regular fa-image';
  static shortcut = 'Cmd+shift+i';

  static available({ controller }: ToolArgsWithController): boolean {
    const mediaProvider = controller.ahaEditor.getProvider('media');

    return !!mediaProvider?.canAddAttachments;
  }

  // Open a file picker and insert the selected image into the editor.
  static action({ editor, controller }: ToolArgsWithController): void {
    const mediaProvider = controller.ahaEditor.getProvider('media');
    if (!mediaProvider) return;

    mediaProvider.onChooseMedia(editor);
  }

  static async getNodeProperties(
    type: 'image' | 'video' | 'audio' | 'attachment',
    {
      url,
      mediaSrc,
      mimeType,
      name,
    }: {
      url: string;
      mediaSrc: string;
      mimeType?: string;
      name?: string;
    }
  ): Promise<Partial<UploadableElement>> {
    const nodeProperties: MediaNodeProperties = { media_src: mediaSrc };

    if (type === 'image') {
      const dimensions = await MediaCommon.measureImageDimensions(url);

      nodeProperties.type = 'image';
      nodeProperties.resizer_width = dimensions.width;
      nodeProperties.resizer_height = dimensions.height;
      nodeProperties.resizer_originalWidth = dimensions.width;
      nodeProperties.resizer_originalHeight = dimensions.height;
    } else if (type === 'video') {
      const dimensions = await MediaCommon.measureVideoDimensions(url);

      nodeProperties.type = 'video';
      nodeProperties.resizer_width = dimensions.width;
      nodeProperties.resizer_height = dimensions.height;
      nodeProperties.resizer_originalWidth = dimensions.width;
      nodeProperties.resizer_originalHeight = dimensions.height;
    } else if (type === 'audio') {
      nodeProperties.type = 'audio';
    } else {
      nodeProperties.type = 'attachment';
      nodeProperties.attachment_mimeType = mimeType;
      nodeProperties.attachment_fileName = name || 'Attachment';
    }

    return nodeProperties;
  }

  static mediaTypeFromFileExtension(fileExtension: string) {
    if (ALLOWED_IMAGE_FILE_EXTENSIONS.some((ext) => fileExtension === ext)) {
      return 'image';
    }

    if (ALLOWED_VIDEO_FILE_EXTENSIONS.some((ext) => fileExtension === ext)) {
      return 'video';
    }

    if (ALLOWED_AUDIO_FILE_EXTENSIONS.some((ext) => fileExtension === ext)) {
      return 'audio';
    }

    return 'attachment';
  }

  static mediaTypeFromMimeType(mimeType: string) {
    if (ALLOWED_IMAGE_TYPES.includes(mimeType)) {
      return 'image';
    }

    if (ALLOWED_VIDEO_TYPES.includes(mimeType)) {
      return 'video';
    }

    if (ALLOWED_AUDIO_TYPES.includes(mimeType)) {
      return 'audio';
    }

    return 'attachment';
  }

  static async insertMediaFromUrl(
    editor: Editor,
    {
      url,
      fileExtension,
      mimeType,
      name,
    }: {
      url: string;
      fileExtension: string;
      mimeType?: string;
      name?: string;
    }
  ) {
    const type = ToolInsertMedia.mediaTypeFromFileExtension(fileExtension);

    if (!type) {
      throw new Error(`Unsupported media type: ${fileExtension}`);
    }

    const nodeProperties = await ToolInsertMedia.getNodeProperties(type, {
      url,
      mediaSrc: url,
      mimeType,
      name,
    });

    Transforms.insertNodes(editor, {
      ...nodeProperties,
      // @ts-expect-error TODO: Type needs fixing here
      type,
      children: [{ text: '' }],
    });
  }

  // Insert the image/video/audio in the specified file into the editor at the
  // selection. A temporary data URL is used until the file is uploaded to the
  // server and a URL is returned.
  static async insertMediaFromFile(editor: Editor, file: File) {
    const dataUrl = await convertFileToDataURL(file);
    const temporaryDataUrl = MediaCommon.storeTemporaryData(dataUrl);
    const type = ToolInsertMedia.mediaTypeFromMimeType(file.type);

    if (!type) {
      throw new Error(`Unsupported media type: ${file.type}`);
    }

    const nodeProperties = await ToolInsertMedia.getNodeProperties(type, {
      url: dataUrl,
      mediaSrc: temporaryDataUrl,
      mimeType: file.type,
      name: file.name,
    });

    const positionPreference = localStorage.getItem(
      MediaCommon.mediaPositionPrefKey
    ) as 'inline' | 'block' | null;
    if (
      positionPreference &&
      ['inline', 'block'].includes(positionPreference)
    ) {
      nodeProperties.media_position = positionPreference;
    }

    // Media elements within list items should be inserted inline.
    const listItems = getCurrentListItems(editor);
    if (listItems.length > 0) {
      nodeProperties.media_position = 'inline';
    }

    // Insert the node into the editor with the temporary data while we wait
    // for the upload to the server.
    Transforms.insertNodes(editor, {
      ...nodeProperties,
      // @ts-expect-error TODO: Type needs fixing here
      type,
      children: [{ text: '' }],
    });

    // Remember the current undo entry so we can augment it with the new URL
    // after the upload completes.
    const history = History.getForEditor(editor);
    const undoEntry = history.lastUndoEntry();

    // Use provider to upload the file and get the URL.
    const ahaEditor = AhaEditor.getForEditor(editor);
    if (!ahaEditor) return;

    const mediaProvider = ahaEditor.getProvider('media');
    if (!mediaProvider) return;
    const attachmentUrl = await mediaProvider.onUploadFile(file);

    // Augment the current undo entry so the URL setting is not undone separately.
    history.addUndo(editor, undoEntry, () => {
      if (attachmentUrl) {
        // Replace the temporary data URL with the real one.
        Transforms.setNodes(
          editor,
          { media_src: attachmentUrl },
          {
            at: [],
            match: (n) => isMediaElement(n) && n.media_src === temporaryDataUrl,
          }
        );
      } else {
        // We failed to upload the attachment. Remove the element from the editor.
        Transforms.removeNodes(editor, {
          at: [],
          match: (n) => isMediaElement(n) && n.media_src === temporaryDataUrl,
        });
      }
    });
  }
}

export default ToolInsertMedia;
